/* eslint arrow-body-style: */
import { batchActions } from 'redux-batched-actions';
import { isCancel } from 'api/Helpers';
import { getFilteredProducts as apiGetFilteredProducts } from 'api/Products';
import KioskLog from 'models/KioskLog';
import { FETCH_ERROR, IS_FETCHING, RECEIVE_PRODUCTS } from './constants';
import { actions as productFilterActions } from '../ProductFilter';

/**
 * Indicate that an error was encountered during the fetch.
 * @param {error} fetchError
 */
const updateFetchError = fetchError => ({
	type: FETCH_ERROR,
	fetchError,
});

/**
 * Indicate the number of searches in progress.
 * @param {bool} increment
 */
const updateFetchStatus = increment => ({
	type: IS_FETCHING,
	increment,
});

// Thunks

/**
 * Store the products returned from a successful fetch along with its meta data.
 * @param {int} categoryId
 * @param {array} products - list of products to store
 * @param {object} resultHeader - meta data about the list returned from the server;
 *  such as page number, limit, totalPages, totalCount of products, cacheable, and prev/next links
 */
const receiveProducts = (categoryId, term, products, resultHeader) => {
	// Only store the last fetch time if the response has marked this data cacheable.
	// An undefined lastFetchTime will always trigger a refetch
	const lastFetchTime = resultHeader.cacheable ? Date.now() : undefined;

	return {
		type: RECEIVE_PRODUCTS,
		categoryId,
		term,
		products,
		lastFetchTime,
		resultHeader,
	};
};

/**
 * Fetch a paginated list of products with various filter options set.
 * @param {axios cancel token} cancelToken
 * @param {object} params
 * 	categoryId - category that the products belong to
 * 	page - zero based page number
 * 	limit - number of results per page
 * 	maxPrice - default max int
 * 	orderBy - 0  (Position) - default
 *            5  (Name Ascend)
 *            6  (Name Descend)
 *            10 (Price Ascend)
 *            11 (Price Descend)
 *            15 (Created on date)
 */
const getFilteredProducts = (cancelToken, params) => {
	return dispatch => {
		dispatch(updateFetchStatus(true));
		return apiGetFilteredProducts(cancelToken, params)
			.then(response => {
				dispatch(
					batchActions([
						updateFetchStatus(false),
						receiveProducts(params.categoryId, params.term, response.data.results.products, {
							cacheable: response.data.cacheable,
							prev: response.data.prev,
							next: response.data.next,
							page: response.data.page,
							totalPages: response.data.totalPages,
							totalCount: response.data.totalCount,
							limit: response.data.limit,
						}),
					]),
				);
				if (response.data.filterableOptionIds) {
					dispatch(
						productFilterActions.receiveFilterableOptions(
							params.categoryId || params.term,
							response.data.filterableOptionIds,
						),
					);
				}
			})
			.catch(error => {
				dispatch(updateFetchStatus(false));
				if (isCancel(error)) {
					// Request was canceled.
					return;
				}
				const log = new KioskLog(error, 'Error file location: redux/domain/ProductSearch/actions');
				log.postMessage();
				dispatch(updateFetchError(error));
			});
	};
};

export {
	getFilteredProducts,
	receiveProducts,
	updateFetchError,
	updateFetchStatus,
};
